import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Footer from './Footer';
import HeroMatrix from './HeroMatrix';
import GrowthSection from './GrowthSection';
import AccelerationSection from './AccelerationSection';
import AnchorLink, { TEAM_URL } from './AnchorLink';
import { useCurrency } from './CurrencyProvider';
import { useLanguage } from '../i18n/LanguageContext';

const CONTACT_FORM_URL = '/apply';

// Adds `is-in` to every `.of-reveal` inside the ref'd container once it scrolls into view.
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const els = root.querySelectorAll('.of-reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return ref;
}

const PILLARS = [
  {
    mark: 'Funding',
    href: TEAM_URL,
    title: 'Capital for ambitious founders',
    body: 'We invest in promising companies building useful products and scalable businesses. We back both Cardano-native teams and companies entering the ecosystem, connecting founders with capital, strategic partners, and the broader Draper network to help them scale.',
  },
  {
    mark: 'Growth',
    anchor: 'growth',
    title: 'Technical mentorship and company creation',
    body: 'Our in-house venture studio, Cometa Labs, provides technical mentorship to portfolio companies and incubates new businesses from the ground up. The team works alongside founders on product development, Cardano integrations, and go-to-market execution, while building companies that create opportunities for equity ownership and long-term financial upside for Orion and the Cardano ecosystem.',
  },
  {
    mark: 'Acceleration',
    anchor: 'acceleration',
    title: 'Founder development with Draper University',
    body: 'Through our partner Draper University, we offer accelerator programs and Silicon Valley residencies that connect founders with experienced operators, mentors, and investors. Our goal is to bring new builders into Cardano and help existing teams grow, refine their products, and prepare for their next stage of funding.',
  },
];

export default function OrionLanding() {
  const pageRef = useReveal();
  const { adaPrice } = useCurrency();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // Section anchors such as /#growth, /#acceleration. Old /#dashboard links go to the Dashboard page.
  useEffect(() => {
    if (!location.hash) return undefined;
    const id = location.hash.slice(1);
    if (id === 'dashboard') {
      navigate('/dashboard', { replace: true });
      return undefined;
    }
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => clearTimeout(t);
  }, [location.hash, navigate]);
  const adaDisplay = adaPrice ? `$${Number(adaPrice).toFixed(4)}` : '—';

  return (
    <div ref={pageRef} className="of-page min-h-screen flex flex-col">
      <main className="flex-grow">
        {/* ============================== HERO ============================== */}
        <section className="of-hero of-hero-full of-rails">
          <HeroMatrix />
          <div className="of-frame relative z-[2] w-full pt-24 md:pt-28 pb-14 md:pb-20">
            <div className="max-w-3xl lg:max-w-[46rem]">
              <div className="of-reveal flex flex-wrap items-center gap-2 mb-8">
                <span className="of-chip">
                  <span className="of-chip-key">ADA</span>
                  <span className="of-chip-sub">[USD]</span>
                  {adaDisplay}
                </span>
              </div>

              <h1 className="of-display of-reveal text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5.25rem]">
                Accelerating the next phase of <strong>Cardano adoption</strong>
              </h1>

              <p className="of-lede of-reveal mt-6 md:mt-8 !max-w-none sm:!max-w-[28.5rem] lg:!max-w-[34rem] xl:!max-w-[39.75rem]">
                Led by Draper Dragon, Orion Fund supports both Cardano-native builders and companies integrating with Cardano,
                combining investment with active support to help them grow, scale, and drive adoption.
              </p>

              <div className="of-reveal mt-7 flex flex-wrap items-center gap-x-6 gap-y-4">
                <a href="https://www.draperdragon.com/" target="_blank" rel="noopener noreferrer" aria-label="Draper Dragon (opens in a new tab)" className="inline-flex opacity-90 hover:opacity-100 transition-opacity">
                  <img src="/logos/draper-dragon.png" alt="Draper Dragon" className="of-logo-tint h-6 md:h-7 w-auto" />
                </a>
                <span className="h-6 w-px bg-[color:var(--of-line-strong)]" />
                <img src="/logos/cardano-foundation-white.svg" alt="Cardano Foundation" className="of-logo-tint h-7 md:h-8 w-auto" />
              </div>

              <div className="of-reveal mt-9 flex flex-wrap items-center gap-3">
                <a href={TEAM_URL} target="_blank" rel="noopener noreferrer" className="of-btn px-5">
                  Meet the Team <ArrowUpRight className="of-arrow w-4 h-4" />
                </a>
                <Link to={CONTACT_FORM_URL} className="of-btn-ghost px-5">
                  Get in Touch
                </Link>
              </div>
            </div>
          </div>
        </section>


        {/* ======================= LIGHT STATEMENT BAND ===================== */}
        <section className="of-light of-rails">
          <div className="of-frame">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 py-20 md:py-28">
              <div className="lg:col-span-3">
                <span className="of-eyebrow of-reveal">01 — Overview</span>
              </div>
              <div className="lg:col-span-9">
                <p className="of-statement of-reveal text-[1.7rem] sm:text-4xl lg:text-[2.75rem]">
                  The Orion Fund is a multi-year venture fund led by <span className="of-accent">Draper Dragon</span>,
                  backing companies at the intersection of <span className="of-accent">blockchain infrastructure</span> and
                  real-world adoption.
                </p>
                <div className="of-overview-boxes of-reveal mt-12 md:mt-16">
                  <div className="of-overview-box">
                    <div className="of-overview-label"><b>01</b> How we support</div>
                    <p>
                      Through direct investment, incubations, and accelerator programs with Draper University, we give founders the
                      capital, expertise, and connections to build and scale.
                    </p>
                  </div>
                  <div className="of-overview-box">
                    <div className="of-overview-label"><b>02</b> Who we back</div>
                    <p>
                      We support Cardano-native teams and welcome companies from outside the ecosystem, helping them explore and
                      develop meaningful Cardano integrations. Our goal is to strengthen existing businesses and bring new builders,
                      products, and users into Cardano.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-end justify-between pb-6">
              <span className="of-eyebrow of-reveal">02 — Our model</span>
            </div>
          </div>

          <div className="md:px-5">
            <div className="of-pillars">
              {PILLARS.map((p) => {
                const inner = (
                  <>
                    <ArrowUpRight className="of-pillar-arrow" aria-hidden="true" />
                    <div className="of-pillar-mark">{p.mark}</div>
                    {p.label && <div className="of-pillar-label">{p.label}</div>}
                    <h3>{p.title}</h3>
                    <p>{p.body}</p>
                  </>
                );
                const cls = 'of-pillar of-pillar-link of-reveal group';
                const label = `${p.mark}: ${p.title}`;
                return p.href ? (
                  <a key={p.title} href={p.href} target="_blank" rel="noopener noreferrer" className={cls} aria-label={label}>{inner}</a>
                ) : (
                  <AnchorLink key={p.title} id={p.anchor} className={cls} aria-label={label}>{inner}</AnchorLink>
                );
              })}
            </div>
          </div>
          <div className="h-16 md:h-24" />
        </section>

        {/* ===================== ONE-PAGE SECTIONS (anchors) ================= */}
        <GrowthSection />
        <AccelerationSection />

        {/* ============================ CLOSING CTA ========================= */}
        <section className="of-rails relative border-b border-[color:var(--of-line)]">
          <div className="of-frame py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="of-eyebrow of-reveal">Build with us</span>
              <h2 className="of-display of-reveal mt-6 text-4xl sm:text-5xl lg:text-6xl">
                Building something ambitious? <strong>Let's talk.</strong>
              </h2>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end of-reveal">
              <Link to={CONTACT_FORM_URL} className="of-btn px-5">
                Get in Touch <ArrowUpRight className="of-arrow w-4 h-4" />
              </Link>
              <a href={TEAM_URL} target="_blank" rel="noopener noreferrer" className="of-btn-ghost px-5">
                Meet the Team
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
